import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { KeyRound, Copy, Check } from 'lucide-react';

interface TenantSecurityCardProps {
  resetModalOpen: boolean;
  onCloseResetModal: () => void;
  newPassword: string;
  setNewPassword: (p: string) => void;
  isResettingPassword: boolean;
  generatedResetUrl: string | null;
  onResetPassword: (e: React.FormEvent) => void;
  onOpenResetModal: () => void;
}

export const TenantSecurityCard: React.FC<TenantSecurityCardProps> = ({
  resetModalOpen,
  onCloseResetModal,
  newPassword,
  setNewPassword,
  isResettingPassword,
  generatedResetUrl,
  onResetPassword,
  onOpenResetModal,
}) => {
  const [copiedResetUrl, setCopiedResetUrl] = useState(false);

  const copyResetUrl = () => {
    if (!generatedResetUrl) return;
    navigator.clipboard.writeText(generatedResetUrl);
    setCopiedResetUrl(true);
    setTimeout(() => setCopiedResetUrl(false), 2000);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            <KeyRound size={16} className="text-cf-orange" />
            <span>Credentials & Authentication</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-cf-textSecondary leading-relaxed">
            As a platform superadministrator, you can directly override this tenant's password or generate an emergency one-time cryptographic reset URL valid for 1 hour.
          </p>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<KeyRound size={13} />}
            onClick={onOpenResetModal}
          >
            Initiate Password Reset
          </Button>
        </CardContent>
      </Card>

      <Modal
        isOpen={resetModalOpen}
        onClose={onCloseResetModal}
        title="Administrative Password Reset"
        maxWidth="md"
      >
        <form onSubmit={onResetPassword} className="space-y-4 text-xs">
          {generatedResetUrl ? (
            <div className="space-y-3">
              <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                A one-time cryptographic password reset link has been generated with a 1-hour expiration.
              </div>
              <div className="flex items-center gap-2">
                <Input value={generatedResetUrl} readOnly mono size="xs" />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={copyResetUrl}
                  leftIcon={copiedResetUrl ? <Check size={13} /> : <Copy size={13} />}
                >
                  {copiedResetUrl ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-cf-textSecondary leading-relaxed">
                Provide a new password to directly override the account credentials, or leave it blank to generate a 1-hour cryptographic reset link.
              </p>
              <Input
                label="Direct Override Password (Optional)"
                type="password"
                placeholder="Leave blank to generate link"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-cf-border">
                <Button variant="secondary" size="sm" onClick={onCloseResetModal}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" isLoading={isResettingPassword}>
                  {newPassword.trim() ? 'Update Password' : 'Generate Reset Link'}
                </Button>
              </div>
            </>
          )}
        </form>
      </Modal>
    </>
  );
};
